import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { lighten, darken, rgba } from 'polished';

import { blue, red } from '~/theseus/styles/core';

interface ButtonProps {
  variant?: 'secondary' | 'plain' | 'warning' | 'primary';
  size?: 'md' | 'lg';
}

const sizeStyles = ({ size }: ButtonProps) => {
  switch (size) {
    case 'lg': {
      return css`
        font-size: 16px;
        padding: 10px 20px;
      `;
    }
    case 'md': {
      return css`
        font-size: 14px;
        padding: 8px 16px;
      `;
    }
    default: {
      return css`
        font-size: 12px;
        padding: 5px 10px;
      `;
    }
  }
};

const colorWithTransition = (key: string) => (color: string) => css`
  ${key}: ${color};

  &:hover,
  &:focus {
    ${key}: ${lighten(0.07, color)};
  }
`;

const backgroundWithTransition = colorWithTransition('background-color');
const textWithTransition = colorWithTransition('color');

const buttonStyles = (backgroundColor, color = rgba('#fff', 0.9)) => css`
  ${backgroundWithTransition(backgroundColor)}
  ${textWithTransition(color)}
`;

const variantStyles = ({ variant }: ButtonProps) => {
  switch (variant) {
    case 'secondary': {
      return buttonStyles('#42b72a');
    }
    case 'plain': {
      return buttonStyles(darken(0.05, '#fff'), rgba('#000', 0.8));
    }
    case 'warning': {
      return buttonStyles(red);
    }
    case 'primary':
    default: {
      return buttonStyles(blue);
    }
  }
};

const Button = styled.button<ButtonProps>`
  font-weight: 600;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px #00000019;
  border: 0;
  cursor: pointer;
  transition: all 200ms ease;

  &:disabled {
    opacity: 0.5;
  }

  ${variantStyles}
  ${sizeStyles}
`;

export default Button;
